import React from 'react';
import {
  DashboardOutlined,
  ThunderboltOutlined,
  AlertOutlined,
  AppstoreOutlined,
  TeamOutlined,
  FileTextOutlined,
  DatabaseOutlined,
  EnvironmentOutlined,
  FileProtectOutlined,
  ExperimentOutlined,
  UploadOutlined,
  ToolOutlined,
  DownloadOutlined,
  ControlOutlined,
  TrophyOutlined,
  CustomerServiceOutlined,
  AuditOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import {
  IconBriefcase,
  IconListCheck,
  IconShieldLock,
} from '@tabler/icons-react';
import { SystemRoleName } from '@evimo/types/enums';
import { PermissionKey } from '@evimo/types/enums/permission-key';

export type NavChildItem = {
  href: string;
  labelKey: string;
  icon?: React.ReactNode;
  /** When set, child is shown only for these roles */
  visibleForRoles?: string[];
  /** Roles for which this child should be hidden */
  hiddenForRoles?: string[];
};

export type NavItem = {
  href: string;
  labelKey: string;
  icon: React.ReactNode;
  children?: NavChildItem[];
  /** Roles for which this nav item should be hidden */
  hiddenForRoles?: string[];
  /** When set, item is shown only for these roles */
  visibleForRoles?: string[];
  /** User must have all listed permissions to see this item */
  requiredPermissions?: PermissionKey[];
};

export const navItems: NavItem[] = [
  {
    href: '/home',
    labelKey: 'dashboard',
    icon: <DashboardOutlined style={{ fontSize: 18 }} />,
  },
  {
    href: '/operator/portfolio',
    labelKey: 'operator',
    icon: <ControlOutlined style={{ fontSize: 18 }} />,
    visibleForRoles: [SystemRoleName.ORGANIZATION_MANAGER],
    children: [
      {
        href: '/operator/portfolio',
        labelKey: 'operatorPortfolio',
        icon: <IconBriefcase size={16} />,
      },
      {
        href: '/operator/scorecard',
        labelKey: 'operatorScorecard',
        icon: <TrophyOutlined style={{ fontSize: 16 }} />,
      },
      {
        href: '/operator/service-quality',
        labelKey: 'operatorServiceQuality',
        icon: <CustomerServiceOutlined style={{ fontSize: 16 }} />,
      },
      {
        href: '/operator/compliance',
        labelKey: 'operatorCompliance',
        icon: <AuditOutlined style={{ fontSize: 16 }} />,
      },
      {
        href: '/operator/reports',
        labelKey: 'operatorReports',
        icon: <BarChartOutlined style={{ fontSize: 16 }} />,
      },
    ],
  },
  {
    href: '/sessions',
    labelKey: 'sessions',
    icon: <ThunderboltOutlined style={{ fontSize: 18 }} />,
    visibleForRoles: [
      SystemRoleName.ORGANIZATION_MANAGER,
      SystemRoleName.SUPERVISOR,
    ],
  },
  {
    href: '/incidents',
    labelKey: 'incidents',
    icon: <AlertOutlined style={{ fontSize: 18 }} />,
  },
  {
    href: '/sla/landlords',
    labelKey: 'sla',
    icon: <FileProtectOutlined style={{ fontSize: 18 }} />,
    children: [
      {
        href: '/sla/landlords',
        labelKey: 'slaLandlords',
      },
      {
        href: '/sla/oems',
        labelKey: 'slaOems',
      },
    ],
    requiredPermissions: [PermissionKey.PARTNERS_LANDLORDS_READ],
  },
  {
    href: '/kms',
    labelKey: 'knowledgeAssistant',
    icon: <FileTextOutlined style={{ fontSize: 18 }} />,
    requiredPermissions: [PermissionKey.DOCUMENTS_READ],
  },
  {
    href: '/assets',
    labelKey: 'assets',
    icon: <AppstoreOutlined style={{ fontSize: 18 }} />,
    children: [
      {
        href: '/assets',
        labelKey: 'assetsList',
      },
      {
        href: '/assets/import',
        labelKey: 'assetImport',
      },
      {
        href: '/assets/custom-fields',
        labelKey: 'assetCustomFields',
        // Definition CRUD is SUPER_ADMIN-only (API RolesGuard).
        visibleForRoles: [SystemRoleName.SUPER_ADMIN],
      },
    ],
  },
  {
    href: '/sites',
    labelKey: 'locations',
    icon: <EnvironmentOutlined style={{ fontSize: 18 }} />,
    children: [
      {
        href: '/sites',
        labelKey: 'sites',
      },
      {
        href: '/zones',
        labelKey: 'zones',
      },
    ],
  },
  {
    href: '/maintenance/preventive',
    labelKey: 'maintenance',
    icon: <ToolOutlined style={{ fontSize: 18 }} />,
    children: [
      {
        href: '/maintenance/preventive',
        labelKey: 'maintenancePreventive',
      },
      {
        href: '/maintenance/tasks-triggers',
        labelKey: 'maintenanceTasksTriggers',
      },
      {
        href: '/maintenance/work-orders/kanban',
        labelKey: 'maintenanceWorkOrdersKanban',
      },
      {
        href: '/maintenance/work-orders/optimizer',
        labelKey: 'maintenanceWorkOrdersOptimizer',
      },
    ],
    requiredPermissions: [
      PermissionKey.MAINTENANCE_READ,
      PermissionKey.WORK_ORDER_READ,
    ],
  },
  {
    href: '/admin/pipeline-debug',
    labelKey: 'pipelineDebug',
    icon: <ExperimentOutlined style={{ fontSize: 18 }} />,
    visibleForRoles: [SystemRoleName.SUPER_ADMIN],
  },
  {
    href: '/admin/logs-export',
    labelKey: 'logsExport',
    icon: <DownloadOutlined style={{ fontSize: 18 }} />,
    visibleForRoles: [SystemRoleName.SUPER_ADMIN],
  },
  {
    href: '/manual-upload',
    labelKey: 'manualUpload',
    icon: <UploadOutlined style={{ fontSize: 18 }} />,
    visibleForRoles: [SystemRoleName.SUPERVISOR],
  },
  {
    href: '/people',
    labelKey: 'people',
    icon: <TeamOutlined style={{ fontSize: 18 }} />,
    hiddenForRoles: [SystemRoleName.SUPER_ADMIN],
  },
  {
    href: '/admin/diagnostic-rules',
    labelKey: 'diagnosticRules',
    icon: <IconListCheck size={18} />,
    requiredPermissions: [PermissionKey.DIAGNOSTIC_RULES_READ],
  },
  {
    href: '/admin/roles',
    labelKey: 'rolesAndPermissions',
    icon: <IconShieldLock size={18} />,
    requiredPermissions: [PermissionKey.ROLES_READ],
  },
  {
    href: '/admin/component-schema',
    labelKey: 'knowledgeBase',
    icon: <DatabaseOutlined style={{ fontSize: 18 }} />,
    hiddenForRoles: [
      SystemRoleName.ORGANIZATION_MANAGER,
      SystemRoleName.FIELD_TECHNICIAN,
      SystemRoleName.SUPERVISOR,
    ],
    children: [
      {
        href: '/admin/component-schema',
        labelKey: 'assetsAndComponents',
      },
      {
        href: '/admin/error-management',
        labelKey: 'errorManagement',
        visibleForRoles: [SystemRoleName.SUPER_ADMIN],
      },
    ],
  },
];
